/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, exhaustMap, map, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AxiosError } from 'axios';
import { usersClient } from '../api/users/usersClient';
import {
    AdminUsersAction,
    AdminUsersActionType,
    downloadUsersAsExcelSuccessAction,
    downloadUsersAsExcelErrorAction,
    fetchUsersListSuccessAction,
    fetchUsersListErrorAction,
    updateUserSuccessAction,
    updateUserErrorAction,
    addUserSuccessAction,
    addUserErrorAction,
} from '../actions/adminUsersActions';
import { AdminUsersState } from '../reducers/adminUsersReducer';
import { DetailedUsersListQueryParams } from '../api/users/usersClient.typings';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

export const getUsersListEpic: Epic<AdminUsersAction> = (action$, state) => {
    return action$.pipe(
        ofType(
            AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE,
            AdminUsersActionType.APPLY_PAGING,
            AdminUsersActionType.APPLY_FILTER,
        ),
        switchMap(() => {
            const queryParams = makeUsersListQueryParamsFromState(state.value.adminUsersReducer);
            return from(usersClient.listDetailedUsers(queryParams)).pipe(
                map((resp) => fetchUsersListSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(fetchUsersListErrorAction(parseError(err, i18n.t('SystemScreens.errors.usersNotFound')))),
                ),

                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};

export const usersListDownloadEpic: Epic<AdminUsersAction> = (actions$, state$) => {
    return actions$.pipe(
        ofType(AdminUsersActionType.DOWNLOAD_EXCEL),
        exhaustMap(() => {
            const fileName = 'usersList.xlsx';
            const queryParams = { ...state$.value.adminUsersReducer.filterParams };
            return from(usersClient.downloadUsersAsExcel(queryParams)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadUsersAsExcelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadUsersAsExcelErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const editUserEpic: Epic<AdminUsersAction> = (action$) => {
    return action$.pipe(
        ofType(AdminUsersActionType.EDIT_USER),
        switchMap((action) => {
            return from(usersClient.updateDetailedUser(action.payload.userId.toString(), action.payload.userInfo)).pipe(
                map((resp) => updateUserSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        updateUserErrorAction(
                            parseError(err, i18n.t('SystemScreens.errors.updateUserInfoFailed'), true),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};

export const blockUserEpic: Epic<AdminUsersAction> = (action$) => {
    return action$.pipe(
        ofType(AdminUsersActionType.BLOCK_USER),
        switchMap((action) => {
            const userId = action.payload;

            return from(usersClient.blockUser(userId.toString())).pipe(
                map((resp) => updateUserSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        updateUserErrorAction(
                            parseError(err, i18n.t('SystemScreens.errors.blockUserFailed', { searchText: userId })),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};

export const unblockUserEpic: Epic<AdminUsersAction> = (action$) => {
    return action$.pipe(
        ofType(AdminUsersActionType.UNBLOCK_USER),
        switchMap((action) => {
            const userId = action.payload;

            return from(usersClient.unblockUser(userId.toString())).pipe(
                map((resp) => updateUserSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        updateUserErrorAction(
                            parseError(err, i18n.t('SystemScreens.errors.unblockUserFailed', { searchText: userId })),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};

export const deleteUserEpic: Epic<AdminUsersAction> = (action$) => {
    return action$.pipe(
        ofType(AdminUsersActionType.DELETE_USER),
        switchMap((action) => {
            const userId = action.payload;

            return from(usersClient.deleteUser(userId.toString())).pipe(
                map((resp) => updateUserSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        updateUserErrorAction(
                            parseError(err, i18n.t('SystemScreens.errors.deleteUserFailed', { searchText: userId })),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};

export const addUserEpic: Epic<AdminUsersAction> = (action$) => {
    return action$.pipe(
        ofType(AdminUsersActionType.ADD_USER),
        switchMap((action) => {
            return from(usersClient.addUser(action.payload)).pipe(
                map((resp) => addUserSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(addUserErrorAction(parseError(err, i18n.t('SystemScreens.errors.addUserFailed'), true))),
                ),
                takeUntil(action$.ofType(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE)),
            );
        }),
    );
};
function makeUsersListQueryParamsFromState(state: AdminUsersState): DetailedUsersListQueryParams {
    return {
        ...state.pageParams,
        ...state.filterParams,
    };
}
